// ======================================================================
// Component: RuleDrawer
// Purpose: Create / Edit Automation Rule
// ======================================================================

import React, { useEffect, useState } from "react";
import ruleService from "../services/ruleService";
import ConditionBuilder from "./ConditionBuilder";

export default function RuleDrawer({ open, onClose, onSaved }) {

  // ------------------------------------------------------------
  // FORM STATE
  // ------------------------------------------------------------
  const [form, setForm] = useState({
    rule_name: "",
    trigger_event: "",
    action_type: "CREATE_FOLLOWUP",
    is_active: true
  });

  const [conditions, setConditions] = useState([]);
  const [eventFields, setEventFields] = useState([]);
  const [saving, setSaving] = useState(false);

  // ------------------------------------------------------------
  // RESET DRAWER WHEN OPENED
  // ------------------------------------------------------------
  useEffect(() => {
    if (open) {
      setForm({
        rule_name: "",
        trigger_event: "",
        action_type: "CREATE_FOLLOWUP",
        is_active: true
      });
      setConditions([]);
      setEventFields([]);
    }
  }, [open]);

  // ------------------------------------------------------------
  // HANDLE CHANGE
  // ------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // ------------------------------------------------------------
  // LOAD EVENT FIELDS WHEN EVENT CHANGES
  // ------------------------------------------------------------
  useEffect(() => {

    const loadFields = async () => {
      try {
        if (!form.trigger_event) {
          setEventFields([]);
          return;
        }

        const res = await ruleService.getEventFields(
          form.trigger_event
        );

        setEventFields(res.data || []);

      } catch (err) {
        console.error("Failed loading event fields", err);
      }
    };

    loadFields();

  }, [form.trigger_event]);

  // ------------------------------------------------------------
  // SAVE RULE
  // ------------------------------------------------------------
  const handleSave = async () => {

    try {
      setSaving(true);

      const payload = {
        ...form,
        conditions_json: JSON.stringify(conditions)
      };

      await ruleService.createRule(payload);

      alert("Rule created successfully");

      onSaved && onSaved();
      onClose();

    } catch (err) {
      console.error(err);
      alert("Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------
  // IMPORTANT: Hooks must run before conditional return
  // ------------------------------------------------------------
  if (!open) return null;

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 420,
        height: "100%",
        background: "#fff",
        boxShadow: "-4px 0 20px rgba(0,0,0,0.2)",
        zIndex: 1050,
        padding: 20,
        overflowY: "auto"
      }}
    >

      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h5>Create Automation Rule</h5>
        <button className="btn btn-sm btn-light" onClick={onClose}>
          ✕
        </button>
      </div>

      <hr />

      {/* RULE NAME */}
      <div className="mb-3">
        <label className="form-label">Rule Name</label>
        <input
          type="text"
          name="rule_name"
          className="form-control"
          value={form.rule_name}
          onChange={handleChange}
        />
      </div>

      {/* EVENT TYPE */}
      <div className="mb-3">
        <label className="form-label">Trigger Event</label>
        <select
          name="trigger_event"
          className="form-select"
          value={form.trigger_event}
          onChange={handleChange}
        >
          <option value="">Select Event</option>
          <option value="PO_APPROVED">PO Approved</option>
          <option value="GRN_POSTED">GRN Posted</option>
          <option value="INDENT_CREATED">Indent Created</option>
        </select>
      </div>

      {/* ACTION */}
      <div className="mb-3">
        <label className="form-label">Action Type</label>
        <select
          name="action_type"
          className="form-select"
          value={form.action_type}
          onChange={handleChange}
        >
          <option value="CREATE_FOLLOWUP">Create Followup</option>
          <option value="SEND_EMAIL">Send Email</option>
        </select>
      </div>

      {/* CONDITION BUILDER */}
      {eventFields.length > 0 && (
        <div className="mb-3">
          <ConditionBuilder
            conditions={conditions}
            setConditions={setConditions}
            eventFields={eventFields}
          />
        </div>
      )}

      {/* STATUS */}
      <div className="form-check mb-4">
        <input
          type="checkbox"
          className="form-check-input"
          checked={form.is_active}
          onChange={(e) =>
            setForm(prev => ({
              ...prev,
              is_active: e.target.checked
            }))
          }
        />
        <label className="form-check-label">
          Active Rule
        </label>
      </div>

      {/* FOOTER */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>

        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Rule"}
        </button>
      </div>

    </div>
  );
}
